import { useEffect, useRef }                from "react";
import { useToastMessage }                  from "components/Covers/toastMessage";
import { GetSystemLabel }                   from "Utilities/utils";

// Single shared slot: only one in-progress editing draft is kept at a time.
// Opening a different editing form discards whatever draft was left behind,
// so this never leaks a draft from one record/entity into another.
const DRAFT_STORAGE_KEY = "EditDraft";
const DEFAULT_EDIT_DRAFT_INTERVAL_MS = 30000;

const readStoredDraft = () => {
    try {
        const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    }
    catch (error) {
        return null;
    }
};

// Returns the saved data for draftKey (leaving it in storage). If the stored
// draft belongs to a different editing session, it is discarded and null is
// returned, since that draft no longer applies to what's being opened now.
export const takeEditDraft = (draftKey) => {
    const stored = readStoredDraft();
    if (!stored || !draftKey) return null;
    if (stored.draftKey !== draftKey) {
        try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (error) {}
        return null;
    }
    return stored.data;
};

// Drops the stored draft, e.g. once its record has been saved successfully.
export const clearEditDraft = () => {
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (error) {}
};

// On an interval (GetEditDraftIntervalMs, default 30s), while `needSave` is
// true, persists `data` under `draftKey` so an accidental close of the
// editing form doesn't lose the in-progress changes.
export const useEditDraftAutoSave = ({ draftKey, data, needSave, enabled = true }) => {
    const { showToastMessage } = useToastMessage();
    const dataRef              = useRef(data);
    const needSaveRef          = useRef(needSave);
    dataRef.current            = data;
    needSaveRef.current        = needSave;

    useEffect(() => {
        if (!enabled || !draftKey) return undefined;
        const intervalId = setInterval(() => {
            if (!needSaveRef.current) return;
            try {
                localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
                    draftKey,
                    data: dataRef.current,
                    savedAt: Date.now(),
                }));
                showToastMessage({ header: GetSystemLabel("Warning"), message: GetSystemLabel("DraftAutoSaved") });
            }
            catch (error) {
                // localStorage unavailable/full: skip this cycle silently.
            }
        }, DEFAULT_EDIT_DRAFT_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [draftKey, enabled, showToastMessage]);
};
